import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { UserIcon, ShieldIcon, Settings2Icon, TrashIcon, RadioIcon, PlusIcon, CopyIcon, RefreshCwIcon, CheckIcon, UploadIcon, UsersIcon, DownloadIcon, XIcon, AlertCircleIcon, KeyRoundIcon } from 'lucide-react';
import { generateReadablePassword } from '@/lib/utils/password-generator';
import type { User, Station } from '@shared/schema';
import ExcelJS from 'exceljs';

interface BulkUserEntry {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  stationIds: string[];
}

export function UserManagement() {
  const { toast } = useToast();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [addingUser, setAddingUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'user'>('user');
  const [forcePasswordReset, setForcePasswordReset] = useState(false);
  const [editingFirstName, setEditingFirstName] = useState('');
  const [editingLastName, setEditingLastName] = useState('');
  const [editingLeaderboardExempt, setEditingLeaderboardExempt] = useState(false);
  const [userStations, setUserStations] = useState<string[]>([]);
  
  // Credentials modal state (shared by Add User and Reset Password; the
  // password lives only in this state and is cleared when the modal closes)
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentialsMode, setCredentialsMode] = useState<'created' | 'reset'>('created');
  const [createdUserEmail, setCreatedUserEmail] = useState('');
  const [createdUserPassword, setCreatedUserPassword] = useState('');
  const [credentialsCopied, setCredentialsCopied] = useState(false);

  // Password reset confirm dialog state
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Bulk add users state
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkUsers, setBulkUsers] = useState<BulkUserEntry[]>([]);
  const [bulkResults, setBulkResults] = useState<{
    success: Array<{ email: string; firstName: string; lastName: string; password: string; stations: string[]; userId: string }>;
    failed: Array<{ email: string; firstName: string; lastName: string; error: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch all users
  const { data: users = [], isLoading: loadingUsers } = useQuery<User[]>({
    queryKey: ['/api/users'],
  });

  // Fetch all stations for assignment
  const { data: allStations = [] } = useQuery<Station[]>({
    queryKey: ['/api/stations'],
  });

  // Fetch stations for specific user when editing
  const { data: assignedStations = [] } = useQuery<Station[]>({
    queryKey: ['/api/users', editingUser?.id, 'stations'],
    enabled: !!editingUser,
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, updates }: { userId: string; updates: Partial<User> }) => {
      return apiRequest(`/api/users/${userId}`, 'PATCH', updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'], refetchType: 'active' });
      toast({
        title: 'User updated',
        description: 'User role has been updated successfully.',
      });
      setEditingUser(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update user.',
        variant: 'destructive',
      });
    },
  });

  // Reset password mutation: the temporary password is generated client-side,
  // sent in the request body, and never comes back in the response.
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: string; email: string; password: string }) => {
      return apiRequest(`/api/users/${userId}/reset-password`, 'POST', { password });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'], refetchType: 'active' });
      setEditingUser(null);
      setCredentialsMode('reset');
      setCreatedUserEmail(variables.email);
      setCreatedUserPassword(variables.password);
      setShowCredentials(true);
    },
    onError: (error: any) => {
      toast({
        title: 'Password reset failed',
        description: error.message || 'Failed to reset the password.',
        variant: 'destructive',
      });
    },
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async ({ email, firstName, lastName, password, role, requirePasswordReset }: { 
      email: string; 
      firstName: string; 
      lastName: string; 
      password: string; 
      role: 'admin' | 'user';
      requirePasswordReset: boolean;
    }) => {
      return apiRequest('/api/users', 'POST', { email, firstName, lastName, password, role, requirePasswordReset });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'], refetchType: 'active' });
      
      // Show credentials modal
      setCredentialsMode('created');
      setCreatedUserEmail(variables.email);
      setCreatedUserPassword(variables.password);
      setShowCredentials(true);
      
      // Reset form
      setAddingUser(false);
      setNewUserEmail('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserPassword('');
      setNewUserRole('user');
      setForcePasswordReset(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Creation failed',
        description: error.message || 'Failed to create user.',
        variant: 'destructive',
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return apiRequest(`/api/users/${userId}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'], refetchType: 'active' });
      toast({
        title: 'User deleted',
        description: 'User has been deleted successfully.',
      });
      setDeletingUser(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Delete failed',
        description: error.message || 'Failed to delete user.',
        variant: 'destructive',
      });
    },
  });

  // Assign station mutation
  const assignStationMutation = useMutation({
    mutationFn: async ({ userId, stationId }: { userId: string; stationId: string }) => {
      return apiRequest(`/api/users/${userId}/stations/${stationId}`, 'POST');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users', editingUser?.id, 'stations'], refetchType: 'active' });
    },
  });

  // Remove station mutation
  const removeStationMutation = useMutation({
    mutationFn: async ({ userId, stationId }: { userId: string; stationId: string }) => {
      return apiRequest(`/api/users/${userId}/stations/${stationId}`, 'DELETE');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users', editingUser?.id, 'stations'], refetchType: 'active' });
    },
  });

  // Bulk create users mutation
  const bulkCreateMutation = useMutation({
    mutationFn: async (users: BulkUserEntry[]) => {
      return apiRequest('/api/users/bulk', 'POST', { users });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'], refetchType: 'active' });
      setBulkResults(data);
      toast({
        title: 'Bulk creation complete',
        description: data.message,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Bulk creation failed',
        description: error.message || 'Failed to create users.',
        variant: 'destructive',
      });
    },
  });

  // Handle Excel file upload for bulk users
  const handleBulkFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const buffer = event.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];

        // Skip header row and parse data
        const users: BulkUserEntry[] = [];
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber === 1) return; // header
          const firstName = String(row.getCell(1).value ?? '').trim();
          const lastName = String(row.getCell(2).value ?? '').trim();
          const email = String(row.getCell(3).value ?? '').trim();
          if (firstName || lastName || email) {
            users.push({
              id: crypto.randomUUID(),
              firstName,
              lastName,
              email,
              password: generateReadablePassword(),
              stationIds: [],
            });
          }
        });

        setBulkUsers(users);
        toast({
          title: 'File loaded',
          description: `Found ${users.length} users in the spreadsheet.`,
        });
      } catch (error) {
        toast({
          title: 'Failed to parse file',
          description: 'Please ensure the file is a valid Excel file with columns: First Name, Last Name, Email',
          variant: 'destructive',
        });
      }
    };
    reader.readAsArrayBuffer(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Update bulk user entry
  const updateBulkUser = (id: string, field: keyof BulkUserEntry, value: any) => {
    setBulkUsers(prev => prev.map(user => 
      user.id === id ? { ...user, [field]: value } : user
    ));
  };

  // Toggle station for bulk user
  const toggleBulkUserStation = (userId: string, stationId: string) => {
    setBulkUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const hasStation = user.stationIds.includes(stationId);
      return {
        ...user,
        stationIds: hasStation 
          ? user.stationIds.filter(id => id !== stationId)
          : [...user.stationIds, stationId]
      };
    }));
  };

  // Remove bulk user from list
  const removeBulkUser = (id: string) => {
    setBulkUsers(prev => prev.filter(user => user.id !== id));
  };

  // Add empty user row
  const addEmptyBulkUser = () => {
    setBulkUsers(prev => [...prev, {
      id: crypto.randomUUID(),
      firstName: '',
      lastName: '',
      email: '',
      password: generateReadablePassword(),
      stationIds: [],
    }]);
  };

  // Download credentials as Excel
  const downloadCredentialsExcel = async () => {
    if (!bulkResults?.success.length) return;
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('User Credentials');
    
    // Add headers
    worksheet.addRow(['First Name', 'Last Name', 'Email', 'Password', 'Assigned Stations', 'Login URL']);
    
    // Add data rows
    for (const user of bulkResults.success) {
      worksheet.addRow([
        user.firstName,
        user.lastName,
        user.email,
        user.password,
        user.stations.join(', '),
        window.location.origin,
      ]);
    }
    
    // Auto-width columns
    worksheet.columns = [
      { width: 15 },
      { width: 15 },
      { width: 30 },
      { width: 20 },
      { width: 40 },
      { width: 40 },
    ];
    
    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_credentials_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Downloaded',
      description: 'Credentials spreadsheet has been downloaded.',
    });
  };

  // Reset bulk add state
  const resetBulkAdd = () => {
    setBulkUsers([]);
    setBulkResults(null);
    setBulkAddOpen(false);
  };

  // Generate password when dialog opens
  useEffect(() => {
    if (addingUser) {
      setNewUserPassword(generateReadablePassword());
      setForcePasswordReset(true); // Default to requiring password reset
    }
  }, [addingUser]);
  
  const handleGeneratePassword = () => {
    setNewUserPassword(generateReadablePassword());
  };

  const handleCopyCredentials = () => {
    const text = `Login Credentials:\n\nEmail: ${createdUserEmail}\nPassword: ${createdUserPassword}\n\nPlease sign in at: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    setCredentialsCopied(true);
    setTimeout(() => setCredentialsCopied(false), 2000);
    toast({
      title: 'Credentials copied',
      description: 'Login credentials have been copied to clipboard.',
    });
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditingFirstName(user.firstName || '');
    setEditingLastName(user.lastName || '');
    setEditingLeaderboardExempt(user.leaderboardExempt || false);
    const stationIds = assignedStations.map(s => s.id);
    setUserStations(stationIds);
  };

  const handleRoleChange = (newRole: string) => {
    if (editingUser) {
      updateUserMutation.mutate({
        userId: editingUser.id,
        updates: { role: newRole as 'admin' | 'user' },
      });
    }
  };

  const handleStationToggle = async (stationId: string, checked: boolean) => {
    if (!editingUser) return;

    try {
      if (checked) {
        await assignStationMutation.mutateAsync({ 
          userId: editingUser.id, 
          stationId 
        });
        setUserStations([...userStations, stationId]);
      } else {
        await removeStationMutation.mutateAsync({ 
          userId: editingUser.id, 
          stationId 
        });
        setUserStations(userStations.filter(id => id !== stationId));
      }
      
      toast({
        title: checked ? 'Station assigned' : 'Station removed',
        description: `Station access has been ${checked ? 'granted' : 'removed'}.`,
      });
    } catch (error) {
      toast({
        title: 'Failed to update station access',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  if (loadingUsers) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading users...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Management
          </CardTitle>
          <CardDescription>
            Manage user accounts, roles, and station assignments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => setAddingUser(true)}
                size="sm"
                data-testid="button-add-user"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button
                onClick={() => setBulkAddOpen(true)}
                size="sm"
                variant="outline"
                data-testid="button-bulk-add-users"
              >
                <UsersIcon className="h-4 w-4 mr-2" />
                Bulk Add Users
              </Button>
            </div>
            {users.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No users found.
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`user-row-${user.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {user.role === 'admin' ? (
                          <ShieldIcon className="h-5 w-5 text-primary" />
                        ) : (
                          <UserIcon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{getUserDisplayName(user)}</div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Last active: {formatDate(user.updatedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role || 'user'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                        data-testid={`button-edit-user-${user.id}`}
                      >
                        <Settings2Icon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingUser(user)}
                        data-testid={`button-delete-user-${user.id}`}
                      >
                        <TrashIcon className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Manage user role and station assignments for {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-first-name">First Name</Label>
                  <Input
                    id="edit-first-name"
                    value={editingFirstName}
                    onChange={(e) => setEditingFirstName(e.target.value)}
                    placeholder="First name"
                    data-testid="input-edit-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-last-name">Last Name</Label>
                  <Input
                    id="edit-last-name"
                    value={editingLastName}
                    onChange={(e) => setEditingLastName(e.target.value)}
                    placeholder="Last name"
                    data-testid="input-edit-last-name"
                  />
                </div>
              </div>
              
              {/* Role Selection */}
              <div className="space-y-2">
                <Label>User Role</Label>
                <Select
                  value={editingUser.role || 'user'}
                  onValueChange={handleRoleChange}
                  disabled={updateUserMutation.isPending}
                >
                  <SelectTrigger data-testid="select-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {editingUser.role === 'admin' 
                    ? 'Admin users have full access to all stations and can manage other users.'
                    : 'Regular users can only access stations assigned to them.'}
                </p>
              </div>

              {/* Leaderboard Exemption */}
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="leaderboard-exempt"
                    checked={editingLeaderboardExempt}
                    onCheckedChange={(checked) => setEditingLeaderboardExempt(checked as boolean)}
                    disabled={updateUserMutation.isPending}
                    data-testid="checkbox-leaderboard-exempt"
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor="leaderboard-exempt"
                      className="cursor-pointer"
                    >
                      Remove from leaderboard
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      When checked, this user will see "Exempt" instead of their position on the leaderboard.
                    </p>
                  </div>
                </div>
              </div>

              {/* Password Reset */}
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                  <p className="text-sm text-muted-foreground">
                    Generate a new temporary password. The user is signed out everywhere
                    and must choose a new password at their next sign-in.
                  </p>
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setResettingPassword(true)}
                    disabled={resetPasswordMutation.isPending}
                    data-testid="button-reset-password"
                  >
                    <KeyRoundIcon className="mr-2 h-4 w-4" />
                    {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </div>

              {/* Station Assignments (only for regular users) */}
              {editingUser.role !== 'admin' && (
                <div className="space-y-2">
                  <Label>Station Access</Label>
                  <ScrollArea className="h-[300px] border rounded-md p-4">
                    <div className="space-y-3">
                      {allStations.map((station) => {
                        const isAssigned = assignedStations.some(s => s.id === station.id);
                        return (
                          <div key={station.id} className="flex items-start space-x-3">
                            <Checkbox
                              id={`station-${station.id}`}
                              checked={isAssigned}
                              onCheckedChange={(checked) => 
                                handleStationToggle(station.id, checked as boolean)
                              }
                              disabled={assignStationMutation.isPending || removeStationMutation.isPending}
                              data-testid={`checkbox-station-${station.id}`}
                            />
                            <div className="space-y-1">
                              <Label
                                htmlFor={`station-${station.id}`}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <RadioIcon className="h-4 w-4 text-muted-foreground" />
                                {station.name}
                              </Label>
                              {station.description && (
                                <p className="text-sm text-muted-foreground">
                                  {station.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                  <p className="text-sm text-muted-foreground">
                    Select which stations this user can access. Changes are saved automatically.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditingUser(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (editingUser) {
                  updateUserMutation.mutate({
                    userId: editingUser.id,
                    updates: {
                      firstName: editingFirstName.trim() || null,
                      lastName: editingLastName.trim() || null,
                      leaderboardExempt: editingLeaderboardExempt
                    }
                  });
                }
              }}
              disabled={updateUserMutation.isPending}
              data-testid="button-save-user-changes"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user account for {deletingUser?.email}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingUser && deleteUserMutation.mutate(deletingUser.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Confirmation Dialog */}
      <AlertDialog open={resettingPassword} onOpenChange={setResettingPassword}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password for {editingUser?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This generates a new temporary password, signs the user out of all active
              sessions, and requires them to choose a new password at their next sign-in.
              The temporary password will be shown to you once so you can share it with them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (editingUser) {
                  resetPasswordMutation.mutate({
                    userId: editingUser.id,
                    email: editingUser.email || '',
                    password: generateReadablePassword(),
                  });
                }
              }}
              data-testid="button-confirm-reset-password"
            >
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add User Dialog */}
      <Dialog open={addingUser} onOpenChange={setAddingUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account. They will be able to log in with their email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-user-first-name">First Name</Label>
                <Input
                  id="new-user-first-name"
                  placeholder="John"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  data-testid="input-new-user-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-last-name">Last Name</Label>
                <Input
                  id="new-user-last-name"
                  placeholder="Doe"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  data-testid="input-new-user-last-name"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email Address</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="user@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                data-testid="input-new-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Generated Password</Label>
              <div className="flex gap-2">
                <Input
                  id="new-user-password"
                  type="text"
                  value={newUserPassword}
                  readOnly
                  className="font-mono"
                  data-testid="input-new-user-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleGeneratePassword}
                  data-testid="button-generate-password"
                  title="Generate new password"
                >
                  <RefreshCwIcon className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                A secure password has been automatically generated. You can regenerate it if needed.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="force-password-reset"
                checked={forcePasswordReset}
                onCheckedChange={(checked) => setForcePasswordReset(checked as boolean)}
                data-testid="checkbox-force-password-reset"
              />
              <Label
                htmlFor="force-password-reset"
                className="text-sm font-normal cursor-pointer"
              >
                Force password reset on first login
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-user-role">User Role</Label>
              <Select
                value={newUserRole}
                onValueChange={(value) => setNewUserRole(value as 'admin' | 'user')}
              >
                <SelectTrigger id="new-user-role" data-testid="select-new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {newUserRole === 'admin' 
                  ? 'Admin users have full access to all stations and can manage other users.'
                  : 'Regular users can only access stations assigned to them.'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddingUser(false);
              setNewUserEmail('');
              setNewUserFirstName('');
              setNewUserLastName('');
              setNewUserPassword('');
              setNewUserRole('user');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (newUserEmail.trim() && newUserPassword.trim() && newUserPassword.length >= 6) {
                  createUserMutation.mutate({ 
                    email: newUserEmail.trim(),
                    firstName: newUserFirstName.trim(),
                    lastName: newUserLastName.trim(),
                    password: newUserPassword,
                    role: newUserRole,
                    requirePasswordReset: forcePasswordReset
                  });
                }
              }}
              disabled={!newUserEmail.trim() || !newUserPassword.trim() || newUserPassword.length < 6 || createUserMutation.isPending}
              data-testid="button-create-user"
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Credentials Display Modal (one-time: the password is cleared from
          state as soon as the modal closes and cannot be shown again) */}
      <Dialog
        open={showCredentials}
        onOpenChange={(open) => {
          setShowCredentials(open);
          if (!open) {
            setCreatedUserEmail('');
            setCreatedUserPassword('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {credentialsMode === 'reset' ? 'Temporary Password Generated' : 'User Created Successfully'}
            </DialogTitle>
            <DialogDescription>
              {credentialsMode === 'reset'
                ? "Share this temporary password with the user securely. It is shown only once and won't be shown again."
                : "Share these credentials with the new user. Make sure to save them as they won't be shown again."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Email</p>
                <p className="font-mono text-sm">{createdUserEmail}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  {credentialsMode === 'reset' ? 'Temporary Password' : 'Password'}
                </p>
                <p className="font-mono text-sm" data-testid="text-credentials-password">{createdUserPassword}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Sign In URL</p>
                <p className="font-mono text-sm">{window.location.origin}</p>
              </div>
            </div>

            {(credentialsMode === 'reset' || forcePasswordReset) && (
              <p className="text-sm text-orange-600 dark:text-orange-400">
                ⚠️ {credentialsMode === 'reset'
                  ? 'The user has been signed out everywhere and will be required to choose a new password at their next sign-in.'
                  : 'The user will be required to change their password on first login.'}
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => setShowCredentials(false)}
            >
              Close
            </Button>
            <Button
              onClick={handleCopyCredentials}
              data-testid="button-copy-credentials"
            >
              {credentialsCopied ? (
                <>
                  <CheckIcon className="mr-2 h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <CopyIcon className="mr-2 h-4 w-4" />
                  Copy Credentials
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Users Dialog */}
      <Dialog open={bulkAddOpen} onOpenChange={(open) => !open && resetBulkAdd()}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5" />
              Bulk Add Users
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file with user data or add users manually. Assign stations and create all users at once.
              All users will be required to change their password on first login.
            </DialogDescription>
          </DialogHeader>
          
          {!bulkResults ? (
            <>
              {/* Upload and controls */}
              <div className="flex items-center gap-4 py-4 border-b">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls,.csv"
                  onChange={handleBulkFileUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-upload-excel"
                >
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Upload Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={addEmptyBulkUser}
                  data-testid="button-add-row"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Row
                </Button>
                <div className="text-sm text-muted-foreground ml-auto">
                  Expected columns: First Name, Last Name, Email
                </div>
              </div>
              
              {/* Users table */}
              {bulkUsers.length > 0 ? (
                <ScrollArea className="flex-1 min-h-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="text-left p-2 font-medium">First Name</th>
                          <th className="text-left p-2 font-medium">Last Name</th>
                          <th className="text-left p-2 font-medium">Email</th>
                          <th className="text-left p-2 font-medium">Password</th>
                          <th className="text-left p-2 font-medium min-w-[300px]">Stations</th>
                          <th className="p-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkUsers.map((user) => (
                          <tr key={user.id} className="border-b hover:bg-muted/50">
                            <td className="p-2">
                              <Input
                                value={user.firstName}
                                onChange={(e) => updateBulkUser(user.id, 'firstName', e.target.value)}
                                placeholder="First name"
                                className="h-8"
                                data-testid={`input-bulk-firstname-${user.id}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                value={user.lastName}
                                onChange={(e) => updateBulkUser(user.id, 'lastName', e.target.value)}
                                placeholder="Last name"
                                className="h-8"
                                data-testid={`input-bulk-lastname-${user.id}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                value={user.email}
                                onChange={(e) => updateBulkUser(user.id, 'email', e.target.value)}
                                placeholder="email@example.com"
                                className="h-8"
                                data-testid={`input-bulk-email-${user.id}`}
                              />
                            </td>
                            <td className="p-2">
                              <div className="flex items-center gap-1">
                                <Input
                                  value={user.password}
                                  readOnly
                                  className="h-8 font-mono text-xs"
                                  data-testid={`input-bulk-password-${user.id}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => updateBulkUser(user.id, 'password', generateReadablePassword())}
                                  title="Regenerate password"
                                >
                                  <RefreshCwIcon className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                            <td className="p-2">
                              <div className="flex flex-wrap gap-1">
                                {allStations.map((station) => (
                                  <label
                                    key={station.id}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                                      user.stationIds.includes(station.id)
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted hover:bg-muted/80'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={user.stationIds.includes(station.id)}
                                      onChange={() => toggleBulkUserStation(user.id, station.id)}
                                    />
                                    {station.name}
                                  </label>
                                ))}
                              </div>
                            </td>
                            <td className="p-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => removeBulkUser(user.id)}
                                data-testid={`button-remove-bulk-user-${user.id}`}
                              >
                                <XIcon className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground py-12">
                  <div className="text-center">
                    <UsersIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Upload an Excel file or add users manually</p>
                    <p className="text-sm mt-2">Excel should have columns: First Name, Last Name, Email</p>
                  </div>
                </div>
              )}
              
              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={resetBulkAdd}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const validUsers = bulkUsers.filter(u => u.email.trim());
                    if (validUsers.length === 0) {
                      toast({
                        title: 'No valid users',
                        description: 'Please add at least one user with an email address.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    bulkCreateMutation.mutate(validUsers);
                  }}
                  disabled={bulkUsers.length === 0 || bulkCreateMutation.isPending}
                  data-testid="button-create-bulk-users"
                >
                  {bulkCreateMutation.isPending ? (
                    <>Creating {bulkUsers.filter(u => u.email.trim()).length} users...</>
                  ) : (
                    <>Create {bulkUsers.filter(u => u.email.trim()).length} Users</>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Results view */}
              <div className="flex-1 overflow-auto py-4">
                {bulkResults.success.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-green-600 dark:text-green-400 flex items-center gap-2 mb-3">
                      <CheckIcon className="h-4 w-4" />
                      Successfully Created ({bulkResults.success.length})
                    </h3>
                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                      <div className="text-sm space-y-1">
                        {bulkResults.success.slice(0, 10).map((user, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckIcon className="h-3 w-3 text-green-600" />
                            <span>{user.firstName} {user.lastName}</span>
                            <span className="text-muted-foreground">({user.email})</span>
                            {user.stations.length > 0 && (
                              <span className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                {user.stations.join(', ')}
                              </span>
                            )}
                          </div>
                        ))}
                        {bulkResults.success.length > 10 && (
                          <div className="text-muted-foreground">
                            ... and {bulkResults.success.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {bulkResults.failed.length > 0 && (
                  <div>
                    <h3 className="font-medium text-red-600 dark:text-red-400 flex items-center gap-2 mb-3">
                      <AlertCircleIcon className="h-4 w-4" />
                      Failed ({bulkResults.failed.length})
                    </h3>
                    <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <div className="text-sm space-y-1">
                        {bulkResults.failed.map((user, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <XIcon className="h-3 w-3 text-red-600" />
                            <span>{user.firstName} {user.lastName}</span>
                            <span className="text-muted-foreground">({user.email})</span>
                            <span className="text-red-600 dark:text-red-400 text-xs">
                              - {user.error}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={resetBulkAdd}>
                  Close
                </Button>
                {bulkResults.success.length > 0 && (
                  <Button
                    onClick={downloadCredentialsExcel}
                    data-testid="button-download-credentials"
                  >
                    <DownloadIcon className="h-4 w-4 mr-2" />
                    Download Credentials Excel
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}